import { ArrowLeftOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons'
import { Button, Col, Modal, Row } from 'antd'
import { sha256 } from 'js-sha256'
import * as R from 'ramda'
import React from 'react'
import { compose, withHandlers, withState } from 'recompose'

import { withFeedback, WithShowFeedback } from '../higher-order/feedback'

import PinPlaceholder from './PinPlaceholder'

import './PinEntryModal.css'

type OuterProps = {
  hashedPin: string
  hide(): void
  onSuccessfulEntry(): void
}

type PinEntryModalHandlers = {
  enterDigit(digit: number): void
  backOneDigit(): void
  verifyPin(): void
}

type InnerProps = {
  enteredDigits: number[]
} & OuterProps &
  PinEntryModalHandlers &
  WithShowFeedback

const PinEntryModal: React.FunctionComponent<InnerProps> = ({
  verifyPin,
  hide,
  enterDigit,
  enteredDigits,
  backOneDigit,
  feedback,
}) => {
  // tslint:disable-next-line:jsx-no-lambda
  const NumKey = ({ num }) => <Button onClick={() => enterDigit(num)}>{num}</Button>

  return (
    <Modal
      visible
      title="Enter PIN"
      onCancel={hide}
      footer={
        <>
          <Button key="ok" onClick={verifyPin}>
            OK
          </Button>
          <Button key="close" onClick={hide}>
            Cancel
          </Button>
        </>
      }>
      <Row justify="center">
        <Col>
          <PinPlaceholder length={enteredDigits.length} />
        </Col>
      </Row>
      <Row justify="center" className="pin-lock">
        <Col>
          {feedback === 'open' ? (
            <UnlockOutlined className={`feedback-${feedback}`} />
          ) : (
            <LockOutlined className={`feedback-${feedback}`} />
          )}
        </Col>
      </Row>
      <Row justify="center" className="pin-keypad">
        <Col>
          {R.range(0, 3).map((rowIdx) => (
            <Row key={rowIdx}>
              {R.range(1, 4).map((colNum) => (
                <Col span={8} key={3 * rowIdx + colNum}>
                  <NumKey num={3 * rowIdx + colNum} />
                </Col>
              ))}
            </Row>
          ))}
          <Row>
            <Col offset={8} span={8}>
              <NumKey num={0} />
            </Col>
            <Col span={8}>
              <Button onClick={backOneDigit}>
                <ArrowLeftOutlined />
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>
  )
}

export default compose<InnerProps, OuterProps>(
  withState('enteredDigits', 'setEnteredDigits', []),
  withFeedback(500),
  withHandlers({
    enterDigit: ({ enteredDigits, setEnteredDigits }) => (digit) =>
      setEnteredDigits([...enteredDigits, digit]),
    backOneDigit: ({ enteredDigits, setEnteredDigits }) => () => {
      if (enteredDigits.length) {
        setEnteredDigits(enteredDigits.slice(0, enteredDigits.length - 1))
      }
    },
    verifyPin: ({
      hashedPin,
      showFeedback,
      hide,
      onSuccessfulEntry,
      setEnteredDigits,
      enteredDigits,
    }) => () => {
      const enteredPin = enteredDigits.reduce((acc, char) => `${acc}${char}`, '')
      const hashedEnteredPin = sha256(enteredPin)

      if (hashedEnteredPin === hashedPin) {
        showFeedback('open').then(() => {
          onSuccessfulEntry()
          hide()
        })
      } else {
        showFeedback('closed').then(() => {
          setEnteredDigits([])
        })
      }
    },
  }),
)(PinEntryModal)



// WEBPACK FOOTER //
// ./src/components/mobile/PinEntryModal.tsx